import React, { useState } from "react";
import { FileText, Link as LinkIcon, Trash2, Download, Loader2, Upload, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import {
  useBuyerReportDocumentsQuery,
  useAddBuyerReportDocumentMutation,
  useDeleteBuyerReportDocumentMutation,
} from "@/hooks/useBuyerReportData";
import type { BuyerReportDocument } from "@/hooks/useBuyerReportData";

interface BuyerReportDocumentsProps {
  reportId: string;
  teamId: string;
  readOnly?: boolean;
}

function formatBytes(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

export function BuyerReportDocuments({ reportId, teamId, readOnly = false }: BuyerReportDocumentsProps) {
  const { data: documents = [], isLoading } = useBuyerReportDocumentsQuery(reportId);
  const addDoc = useAddBuyerReportDocumentMutation(reportId);
  const deleteDoc = useDeleteBuyerReportDocumentMutation(reportId);

  const [mode, setMode] = useState<"upload" | "link">("upload");
  const [isUploading, setIsUploading] = useState(false);
  const [fileDescription, setFileDescription] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [linkLabel, setLinkLabel] = useState("");
  const [linkDescription, setLinkDescription] = useState("");

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_SIZE = 25 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert("File is too large. Maximum size is 25 MB.");
      e.target.value = "";
      return;
    }

    setIsUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const fileExt = file.name.split(".").pop();
      const storagePath = `${user.id}/buyer-report-docs/${reportId}-${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("logos")
        .upload(storagePath, file);
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from("logos").getPublicUrl(storagePath);

      await addDoc.mutateAsync({
        team_id: teamId,
        file_name: file.name,
        file_description: fileDescription || null,
        storage_path: storagePath,
        public_url: publicUrl,
        link_url: null,
        file_size_bytes: file.size,
      });

      setFileDescription("");
      e.target.value = "";
    } catch (err) {
      console.error("Upload failed:", err);
      alert("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handleAddLink = async () => {
    if (!linkUrl.trim() || !linkLabel.trim()) return;
    const rawUrl = linkUrl.trim();
    const normalizedUrl = /^https?:\/\//i.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
    try {
      await addDoc.mutateAsync({
        team_id: teamId,
        file_name: linkLabel.trim(),
        file_description: linkDescription || null,
        storage_path: null,
        public_url: null,
        link_url: normalizedUrl,
        file_size_bytes: null,
      });
      setLinkUrl("");
      setLinkLabel("");
      setLinkDescription("");
    } catch (err) {
      console.error("Failed to add link:", err);
      alert("Failed to add link. Please try again.");
    }
  };

  const handleDelete = async (doc: BuyerReportDocument) => {
    if (!confirm(`Remove "${doc.file_name}"?`)) return;
    try {
      await deleteDoc.mutateAsync(doc);
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Document list */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => {
            const href = doc.link_url ?? doc.public_url;
            const isLink = !!doc.link_url;
            return (
              <div
                key={doc.id}
                className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted/30"
              >
                <div className="flex-shrink-0 text-muted-foreground">
                  {isLink ? <LinkIcon className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{doc.file_name}</p>
                  {doc.file_description && (
                    <p className="text-xs text-muted-foreground truncate">{doc.file_description}</p>
                  )}
                  {doc.file_size_bytes && (
                    <p className="text-xs text-muted-foreground">{formatBytes(doc.file_size_bytes)}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {href && (
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      <Button variant="ghost" size="icon" className="h-7 w-7" title="Open">
                        <Download className="h-3.5 w-3.5" />
                      </Button>
                    </a>
                  )}
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(doc)}
                      title="Remove"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add new (agent only) */}
      {!readOnly && (
        <div className="rounded-lg border border-dashed border-border p-4 space-y-3">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <Button
              variant={mode === "upload" ? "secondary" : "ghost"}
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setMode("upload")}
            >
              <Upload className="h-3.5 w-3.5" />
              Upload File
            </Button>
            <Button
              variant={mode === "link" ? "secondary" : "ghost"}
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setMode("link")}
            >
              <LinkIcon className="h-3.5 w-3.5" />
              Add Link
            </Button>
          </div>

          {mode === "upload" && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Input
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  placeholder="e.g. Inspection report"
                  className="mt-1 text-sm"
                />
              </div>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.webp"
                onChange={handleFileUpload}
                className="hidden"
                id={`buyer-report-doc-upload-${reportId}`}
                disabled={isUploading}
              />
              <Label htmlFor={`buyer-report-doc-upload-${reportId}`}>
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  disabled={isUploading}
                  className="gap-2"
                >
                  <span className="cursor-pointer">
                    {isUploading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5" />
                    )}
                    {isUploading ? "Uploading…" : "Choose File (max 25 MB)"}
                  </span>
                </Button>
              </Label>
            </div>
          )}

          {mode === "link" && (
            <div className="space-y-2">
              <div>
                <Label className="text-xs">Label</Label>
                <Input
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="e.g. Home Inspection Report"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">URL</Label>
                <Input
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://drive.google.com/..."
                  type="url"
                  className="mt-1 text-sm"
                />
              </div>
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Input
                  value={linkDescription}
                  onChange={(e) => setLinkDescription(e.target.value)}
                  placeholder="e.g. Full inspection PDF"
                  className="mt-1 text-sm"
                />
              </div>
              <Button
                size="sm"
                onClick={handleAddLink}
                disabled={!linkUrl.trim() || !linkLabel.trim() || addDoc.isPending}
                className="gap-2"
              >
                {addDoc.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Plus className="h-3.5 w-3.5" />
                )}
                Save Link
              </Button>
            </div>
          )}
        </div>
      )}

      {documents.length === 0 && readOnly && (
        <p className="text-sm text-muted-foreground text-center py-4">No documents attached.</p>
      )}
    </div>
  );
}
